<script lang="ts">
  import { browser } from "$app/environment";
  import { onMount } from "svelte";
  import { goto } from "$app/navigation";
  import { enableAutocomplete } from "$lib/utils/maps";
  import { page } from "$app/stores";
  import { userDetails } from "$lib/consumer/stores/userDetails";
  import type { UserDetails } from "$lib/consumer/stores/userDetails";

  // Props for setting the form visibility and display mode
  export let visible: boolean;
  export let standalone: boolean;

  // References for the form and autocomplete elements
  let addressForm: HTMLFormElement;
  let addressInput: HTMLInputElement;
  let selectedPlace: google.maps.places.PlaceResult | null = null;
  let autocomplete: google.maps.places.Autocomplete;

  // Variables for storing user address details
  let street: UserDetails["street"] = "";
  let city: UserDetails["city"] = "";
  let state: UserDetails["state"] = "";
  let zipCode: UserDetails["zipCode"] = "";
  let country: UserDetails["country"] = "";
  let latitude: UserDetails["latitude"] = null;
  let longitude: UserDetails["longitude"] = null;
  let addressString: UserDetails["addressString"] = "";

  // Initialize the form validation state
  let submittable = false;
  let addressError = false;
  let errorMessage = "Please select an address to continue.";

  // Focuses the input field when the form becomes visible
  $: if (visible && addressInput) {
    setTimeout(() => {
      addressInput?.focus();
    }, 100);
  }

  /**
   * Handles input changes in the delivery address field
   */
  function handleInputChange() {
    // Clear any existing validation errors
    if (addressError) {
      addressError = false;
      addressInput.setCustomValidity("");
    }

    // Mark the form as non-submittable to ensure a place is selected from the autocomplete suggestions
    submittable = false;
  }

  /**
   * Handles the address form submission
   * @param event
   */
  async function handleSubmit(event: SubmitEvent) {
    event.preventDefault();

    // If a place was selected from the autocomplete suggestions
    if (selectedPlace) {
      // Clear any existing validation errors
      addressError = false;

      // Set the form state as submitted
      userDetails.setSubmitted(true);

      // Trigger the GTM submit event
      if (browser && window.dataLayer) {
        window.dataLayer.push({
          event: "address_submit_click",
        } as any);
      }

      // If not already on the check availability page then goto it
      if ($page.url.pathname !== "/check-availability") {
        await goto("/check-availability/");
      }

      // Exit early to prevent further validation checks
      return;
    }

    // If no place is selected and there is text in the input field, then show the validation error
    if (addressInput && addressInput.value.trim()) {
      addressError = true;
      addressInput.setCustomValidity(errorMessage);
      addressInput.reportValidity();
    }
  }

  /**
   * Initialize the delivery address autocompletion
   */
  onMount(async () => {
    // Initialize Google Places autocomplete, restricting it to full addresses in the US only
    if (addressInput) {
      const result = await enableAutocomplete(addressInput, {
        componentRestrictions: { country: "us" },
        types: ["address"],
      });

      // Handle cases where autocomplete returns undefined
      if (result) {
        autocomplete = result;
      } else {
        return;
      }

      // Listen for place selection
      autocomplete.addListener("place_changed", () => {
        // Clear any existing validation errors
        if (addressError) {
          addressError = false;
          addressInput.setCustomValidity("");
        }

        // Autocomplete the address
        const place = autocomplete.getPlace();
        selectedPlace = place;

        // Parse the address components and store the values in the userDetails store
        if (place) {
          // Extract the address components
          if (place.address_components) {
            for (const component of place.address_components) {
              const types = component.types;

              // Get street number and route (street name)
              if (types.includes("street_number")) {
                street = component.long_name + " ";
              } else if (types.includes("route")) {
                street += component.long_name;
              }

              // Get city
              if (types.includes("locality")) {
                city = component.long_name;
              }

              // Get state
              if (types.includes("administrative_area_level_1")) {
                state = component.short_name;
              }

              // Get zip code
              if (types.includes("postal_code")) {
                zipCode = component.long_name;
              }

              // Get country
              if (types.includes("country")) {
                country = component.short_name;
              }
            }
          }

          // Get latitude and longitude
          if (place.geometry?.location) {
            latitude = place.geometry.location.lat();
            longitude = place.geometry.location.lng();
          }

          // Get full address
          if (place.formatted_address) {
            addressString = place.formatted_address;
          }

          // Store the values
          if (latitude !== null && longitude !== null) {
            userDetails.setAddress(
              street,
              city,
              state,
              zipCode,
              country,
              latitude,
              longitude,
              addressString,
            );
          }

          // Set the form as submittable
          submittable = true;
        }
      });
    }
  });
</script>

<form
  class="address-form"
  class:standalone
  class:submittable
  class:visible
  bind:this={addressForm}
  on:submit={handleSubmit}
  novalidate
>
  <input
    class="address-form-input"
    class:standalone
    class:submittable
    type="text"
    placeholder="Enter your delivery address"
    required
    bind:this={addressInput}
    on:input={handleInputChange}
  />

  <button
    type="submit"
    class="address-form-button"
    class:standalone
    class:submittable
    disabled={!submittable}><span class="button-arrow">&rarr;</span></button
  >
</form>

<style type="postcss">
  .address-form {
    display: flex;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 350px;
    margin: 0 auto;
    opacity: 0;
    border: 2px solid #fff;
    border-radius: 2px;
    pointer-events: none;
    overflow: hidden;
    z-index: 1;

    &.visible {
      opacity: 1;
      pointer-events: all;

      @screen md {
        transform: translateX(-50%) scale(1.02);
      }
    }

    &.submittable {
      border-color: rgb(var(--color-consumer-yellow));
    }
  }

  .address-form.standalone {
    position: relative;
    margin: 0;
    border-color: rgba(255, 255, 255, 0.6);

    &.submittable {
      border-color: rgb(var(--color-consumer-yellow));
    }
  }

  /**
   *  Input field
   */

  .address-form-input {
    flex: 1;
    display: flex;
    align-items: center;
    width: 100%;
    height: 65px;
    font-family: Plain;
    font-size: 16px;
    font-weight: 400;
    line-height: 1;
    border: none;
    border-radius: 0;
    padding: 0 20px;

    &::placeholder {
      color: #8a8a8a;
    }

    &:focus {
      outline: none;
    }
  }

  .address-form-input.standalone {
    background-color: rgba(0, 0, 0, 0.2);
    color: #fff;

    &::placeholder {
      color: #fff;
    }
  }

  /**
   * Submit button
   */

  .address-form-button {
    align-items: center;
    justify-content: center;
    width: 65px;
    height: 65px;
    font-family: Plain;
    font-size: 22px;
    font-weight: 200;
    line-height: 1;
    color: rgba(0, 0, 0, 0.4);
    border: none;
    background-color: rgba(255, 255, 255, 0.6);
    transition: all 0.2s ease;

    .button-arrow {
      display: inline-block;
      transition: transform 0.2s ease;
    }

    &.submittable {
      background-color: rgb(var(--color-consumer-yellow));
      color: rgba(0, 0, 0, 1);
      cursor: pointer;

      &:hover {
        background-color: rgb(var(--color-consumer-yellow-bright));
      }

      &:hover .button-arrow {
        transform: scale(1.2);
      }
    }

    /* Only show outline when keyboard focused */
    &:focus-visible {
      outline: 2px solid #000;
      outline-offset: -4px;
    }
  }
</style>
